/**
 * The `spatial-2d` LevelKind (autorunner). Composes: schema (boundary) + 3Cs parse +
 * grammar compile + geometry (§4.2) + multi-body tether feasibility (§4.3). Consumes the
 * 3Cs profile from `ctx.threeCs`; a linear ordered-segment walk, not the general jump-graph.
 */
import type { LevelFinding, LevelKind, LevelValidationCtx } from '../../types.js';
import { type SpatialLevel, SpatialLevelSchema, ThreeCsProfileSchema } from './schema.js';
import { compileTraversalGrammar, effectiveAutoRunSpeed } from './grammar.js';
import { deriveRunways } from './runways.js';
import { checkGeometry, checkTerraces, checkEncounter } from './geometry.js';
import { checkTether } from './tether.js';
import { checkRefs, spatialRefs } from '../../ref-integrity.js';

export const spatialLevelKind: LevelKind<SpatialLevel> = {
  id: 'spatial-2d',
  schema: SpatialLevelSchema,
  consumesThreeCs: true,
  view: 'viewport-2d3d',
  derivation: {
    genres: ['auto-runner', 'platformer'],
    mechanicHints: ['jump', 'run', 'traversal', 'tether', 'obstacle'],
  },
  validate(data: SpatialLevel, ctx: LevelValidationCtx): LevelFinding[] {
    const parsed = ThreeCsProfileSchema.safeParse(ctx.threeCs);
    if (!parsed.success) {
      return [
        {
          severity: 'error',
          code: 'missing-three-cs',
          message: 'spatial-2d requires a valid autorunner-2d 3Cs profile in ctx.threeCs',
        },
      ];
    }
    // Validate the level at the speed it actually runs at (pace ramp), not the base.
    const grammar = compileTraversalGrammar(
      parsed.data,
      effectiveAutoRunSpeed(parsed.data, ctx.levelIndex),
    );
    const runways = deriveRunways(data);
    return [
      ...checkGeometry(data, grammar, runways),
      ...checkTerraces(data, grammar),
      ...checkTether(data, grammar, runways),
      ...checkEncounter(data, grammar, ctx.tags),
      ...checkRefs(spatialRefs(data), ctx.palette),
    ];
  },
};

export {
  SpatialLevelSchema,
  ThreeCsProfileSchema,
  ENTITY_ROLES,
  BiomeSchema,
  EncounterSchema,
  EncounterPhaseSchema,
} from './schema.js';
export type {
  SpatialLevel,
  ThreeCsProfile,
  EntityRole,
  Biome,
  Encounter,
  EncounterPhase,
} from './schema.js';
export { simulatePlaythrough, PRECISION_MARGIN_FRAC } from './playthrough.js';
export type { PlaythroughMetrics } from './playthrough.js';
export {
  compileTraversalGrammar,
  jumpArcHeight,
  arcWidthAboveHeight,
  gapDepth,
  DEEP_GAP_FRACTION,
  type TraversalGrammar,
} from './grammar.js';
